
import { motion } from "framer-motion";
import styled, { keyframes } from "styled-components";

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

const SkeletonBase = styled(motion.div)`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  border-radius: ${props => props.radius || '8px'};
`;

const Container = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const LanguageSkeletonSelector = styled(motion.div)`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 100;
  display: flex;
  gap: 8px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
`;

const LanguageButtonSkeleton = styled(SkeletonBase)`
  width: 40px;
  height: 32px;
  border-radius: 12px;
`;

const ImageContainerSkeleton = styled(motion.div)`
  width: 100%;
  height: 300px;
  position: relative;
  overflow: hidden;
`;

const PetImageSkeleton = styled(SkeletonBase)`
  width: 100%;
  height: 100%;
  border-radius: 0;
`;

const ContentCardSkeleton = styled(motion.div)`
  background: white;
  margin: -40px 16px 0;
  border-radius: 20px;
  position: relative;
  z-index: 10;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  padding: 24px;
`;

const HeaderSkeleton = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
`;

const PetNameSkeleton = styled(SkeletonBase)`
  width: 180px;
  height: 28px;
  margin-bottom: 8px;
  border-radius: 6px;
`;

const LocationSkeleton = styled(SkeletonBase)`
  width: 120px;
  height: 16px;
  border-radius: 4px;
`;

const ButtonGroupSkeleton = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ButtonSkeleton = styled(SkeletonBase)`
  width: 140px;
  height: 36px;
  border-radius: 8px;
`;

const StatsGridSkeleton = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;
`;

const StatCardSkeleton = styled(motion.div)`
  background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }
`;

const StatValueSkeleton = styled(SkeletonBase)`
  width: 60px;
  height: 20px;
  margin: 0 auto 8px;
  border-radius: 4px;
`;

const StatLabelSkeleton = styled(SkeletonBase)`
  width: 40px;
  height: 14px;
  margin: 0 auto;
  border-radius: 4px;
`;

const OwnerCardSkeleton = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border-radius: 12px;
  margin-bottom: 24px;
`;

const OwnerInfoSkeleton = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const OwnerImageSkeleton = styled(SkeletonBase)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
`;

const OwnerTextSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OwnerNameSkeleton = styled(SkeletonBase)`
  width: 100px;
  height: 18px;
  border-radius: 4px;
`;

const OwnerRoleSkeleton = styled(SkeletonBase)`
  width: 60px;
  height: 14px;
  border-radius: 4px;
`;

const ContactButtonsSkeleton = styled(motion.div)`
  display: flex;
  gap: 16px;
`;

const IconButtonSkeleton = styled(SkeletonBase)`
  width: 40px;
  height: 40px;
  border-radius: 8px;
`;

const BioSkeleton = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BioLineSkeleton = styled(SkeletonBase)`
  height: 16px;
  border-radius: 4px;
`;

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const pulseVariants = {
    pulse: {
        scale: [1, 1.02, 1],
        transition: {
            duration: 2,
            ease: "easeInOut",
            repeat: Infinity
        }
    }
};

const PetDetailsSkeleton = () => {
    return (
        <Container>
            {/* Language Selector Skeleton */}
            <LanguageSkeletonSelector
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
            >
                {[1, 2, 3].map((index) => (
                    <LanguageButtonSkeleton
                        key={index}
                        variants={pulseVariants}
                        animate="pulse"
                        style={{ animationDelay: `${index * 0.2}s` }}
                    />
                ))}
            </LanguageSkeletonSelector>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Image Container Skeleton */}
                <ImageContainerSkeleton variants={itemVariants}>
                    <PetImageSkeleton
                        variants={pulseVariants}
                        animate="pulse"
                    />
                </ImageContainerSkeleton>

                {/* Content Card Skeleton */}
                <ContentCardSkeleton variants={itemVariants}>
                    {/* Header Skeleton */}
                    <HeaderSkeleton variants={itemVariants}>
                        <div>
                            <PetNameSkeleton
                                variants={pulseVariants}
                                animate="pulse"
                            />
                            <LocationSkeleton
                                variants={pulseVariants}
                                animate="pulse"
                                style={{ animationDelay: '0.1s' }}
                            />
                        </div>
                        <ButtonGroupSkeleton variants={itemVariants}>
                            <ButtonSkeleton
                                variants={pulseVariants}
                                animate="pulse"
                                style={{ animationDelay: '0.2s' }}
                            />
                            <ButtonSkeleton
                                variants={pulseVariants}
                                animate="pulse"
                                style={{ animationDelay: '0.3s' }}
                            />
                        </ButtonGroupSkeleton>
                    </HeaderSkeleton>

                    {/* Stats Grid Skeleton */}
                    <StatsGridSkeleton variants={itemVariants}>
                        {[1, 2, 3].map((index) => (
                            <StatCardSkeleton
                                key={index}
                                variants={itemVariants}
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <StatValueSkeleton
                                    variants={pulseVariants}
                                    animate="pulse"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                />
                                <StatLabelSkeleton
                                    variants={pulseVariants}
                                    animate="pulse"
                                    style={{ animationDelay: `${index * 0.1 + 0.1}s` }}
                                />
                            </StatCardSkeleton>
                        ))}
                    </StatsGridSkeleton>

                    {/* Owner Card Skeleton */}
                    <OwnerCardSkeleton variants={itemVariants}>
                        <OwnerInfoSkeleton variants={itemVariants}>
                            <OwnerImageSkeleton
                                variants={pulseVariants}
                                animate="pulse"
                            />
                            <OwnerTextSkeleton>
                                <OwnerNameSkeleton
                                    variants={pulseVariants}
                                    animate="pulse"
                                    style={{ animationDelay: '0.1s' }}
                                />
                                <OwnerRoleSkeleton
                                    variants={pulseVariants}
                                    animate="pulse"
                                    style={{ animationDelay: '0.2s' }}
                                />
                            </OwnerTextSkeleton>
                        </OwnerInfoSkeleton>
                        <ContactButtonsSkeleton variants={itemVariants}>
                            <IconButtonSkeleton
                                variants={pulseVariants}
                                animate="pulse"
                                style={{ animationDelay: '0.3s' }}
                            />
                            <IconButtonSkeleton
                                variants={pulseVariants}
                                animate="pulse"
                                style={{ animationDelay: '0.4s' }}
                            />
                        </ContactButtonsSkeleton>
                    </OwnerCardSkeleton>

                    {/* Bio Skeleton */}
                    <BioSkeleton variants={itemVariants}>
                        <BioLineSkeleton
                            style={{ width: '100%' }}
                            variants={pulseVariants}
                            animate="pulse"
                        />
                        <BioLineSkeleton
                            style={{ width: '85%' }}
                            variants={pulseVariants}
                            animate="pulse"
                            style={{ animationDelay: '0.1s' }}
                        />
                        <BioLineSkeleton
                            style={{ width: '70%' }}
                            variants={pulseVariants}
                            animate="pulse"
                            style={{ animationDelay: '0.2s' }}
                        />
                    </BioSkeleton>
                </ContentCardSkeleton>
            </motion.div>
        </Container>
    );
};

export default PetDetailsSkeleton;